import { Link } from "react-router-dom";
import { AlertTriangle, Package } from "lucide-react";
import { getRestockAlerts, formatDaysLeft } from "../lib/vials";

/**
 * RestockAlerts — compact dashboard card that surfaces vials with
 * days_left <= threshold. Renders nothing when no alerts fire so it stays
 * out of the way once inventory is healthy.
 */
export default function RestockAlerts({ vials, peptides, thresholdDays = 7 }) {
  const alerts = getRestockAlerts(vials, peptides, thresholdDays);
  if (!alerts.length) return null;

  const urgent = alerts.filter((a) => (a.forecast?.days ?? 0) <= 3 || (Number(a.vial.remaining_mg ?? a.vial.amount_mg) || 0) <= 0);
  const soon = alerts.filter((a) => !urgent.includes(a));

  return (
    <div
      className="mt-6 border border-[#0D9488]/40 bg-[#0D9488]/5 p-4"
      data-testid="dashboard-restock-alerts"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-4 h-4 ${urgent.length ? "text-[#FF3B30]" : "text-[#0D9488]"}`} />
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488]">
            // Restock Alerts
          </div>
          <span className="font-mono-num text-[10px] text-slate-500">
            {alerts.length} vial{alerts.length === 1 ? "" : "s"} · &lt; {thresholdDays} days left
          </span>
        </div>
        <Link
          to="/inventory"
          className="text-[10px] font-mono-num uppercase tracking-widest text-[#0D9488] hover:underline"
          data-testid="restock-view-inventory"
        >
          Manage inventory →
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {alerts.map(({ vial, forecast, days }) => {
          const remaining = Number(vial.remaining_mg ?? vial.amount_mg) || 0;
          const isEmpty = remaining <= 0;
          const isUrgent = isEmpty || days <= 3;
          const barColor = isEmpty ? "bg-[#FF3B30]" : isUrgent ? "bg-[#FF3B30]" : "bg-[#0D9488]";
          const label = isEmpty ? "Empty" : forecast ? formatDaysLeft(days) : "Empty";
          const runOut = forecast && forecast.days > 0
            ? new Date(Date.now() + forecast.days * 86400000).toLocaleDateString(undefined, { month: "short", day: "numeric" })
            : null;
          return (
            <div
              key={vial.id}
              className={`border p-3 bg-slate-50 ${isUrgent ? "border-[#FF3B30]/40" : "border-slate-200"}`}
              data-testid={`restock-alert-${vial.id}`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Package className="w-3 h-3 text-[#0D9488] shrink-0" />
                  <span className="font-display font-black text-sm uppercase tracking-tight text-slate-900 truncate">
                    {vial.peptide_name}
                  </span>
                </div>
                <span className={`font-mono-num text-xs font-black ${isUrgent ? "text-[#FF3B30]" : "text-[#0D9488]"}`}>
                  {label}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
                <span>
                  {remaining.toFixed(2)} / {Number(vial.amount_mg || 0).toFixed(2)} mg
                </span>
                {runOut && <span>runs out {runOut}</span>}
              </div>
              <div className="h-1 bg-slate-50">
                <div
                  className={`h-full ${barColor}`}
                  style={{ width: `${forecast?.pctRemaining ?? 0}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
